import React, { useMemo } from 'react'
import { Button, Card, Col, Flex, Row, Statistic, Tag, Typography } from 'antd'
import { EditOutlined, ThunderboltOutlined } from '@ant-design/icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { MappingMatrixRow, MappingStats } from '../types'
import { MAPPING_PAIR_ROUTE, MAPPING_WIZARD_ROUTE } from '../mappingRoutes'
import MappingMatrixTable from './MappingMatrixTable'
import MappingLegend from './MappingLegend'

interface CustomerMappingMatrixProps {
  rows: MappingMatrixRow[]
  stats: MappingStats
  loading?: boolean
}

const STAT_COLOR = { complete: '#52c41a', missing: '#c0392b' } as const

const CustomerMappingMatrix: React.FC<CustomerMappingMatrixProps> = ({ rows, stats, loading }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const companies = useMemo(() => rows.map(row => row.company), [rows])

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Flex align="baseline" gap={10}>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('groupCustomerMapping:title', 'Konsernin asiakas- ja toimittajalinkitys')}
          </Typography.Title>
          <Tag>{t('groupCustomerMapping:companyCount', '{{count}} yhtiötä', { count: companies.length })}</Tag>
        </Flex>
        <Flex gap={8}>
          <Button icon={<EditOutlined />} onClick={() => navigate(MAPPING_PAIR_ROUTE)}>
            {t('groupCustomerMapping:editPair', 'Muokkaa linkityksiä')}
          </Button>
          <Button type="primary" icon={<ThunderboltOutlined />} onClick={() => navigate(MAPPING_WIZARD_ROUTE)}>
            {t('groupCustomerMapping:fillMissing', 'Täydennä puuttuvat')}
          </Button>
        </Flex>
      </Flex>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { title: t('groupCustomerMapping:statsTotal', 'Linkityksiä yhteensä'), value: stats.total },
          {
            title: t('groupCustomerMapping:statsComplete', 'Valmiita'),
            value: stats.complete,
            color: STAT_COLOR.complete
          },
          { title: t('groupCustomerMapping:statsMissing', 'Puuttuu'), value: stats.missing, color: STAT_COLOR.missing }
        ].map(stat => (
          <Col xs={12} md={8} key={stat.title}>
            <Card size="small">
              <Statistic
                title={stat.title}
                value={stat.value}
                valueStyle={stat.color ? { color: stat.color } : undefined}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <MappingMatrixTable
        companies={companies}
        rows={rows}
        loading={loading}
        onCellSelect={(ownerId, counterpartyId) => navigate(MAPPING_PAIR_ROUTE, { state: { ownerId, counterpartyId } })}
      />
      <MappingLegend />
    </>
  )
}

export default CustomerMappingMatrix
